import { Datagrid, FunctionField, ListContextProvider, Loading, TextField, useList } from "react-admin";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { read, utils } from 'xlsx';
import { Button, Checkbox, Chip, CircularProgress, Dialog, DialogContent, Divider, Grid, Input, Typography } from "@material-ui/core";
import {
    CloudDone,
    CloudUpload, DetailsOutlined,
    EditAttributesOutlined,
    EditOutlined,
    ErrorOutline,
    SaveAltOutlined
} from "@material-ui/icons";
import { useAssessmentQuestionRepository } from "../../../repositories/assessment-question.repository";
import { CoreTextFieldEllipseComponent } from "../../../components/core/core- text-field-ellipse.component";

export function ImportQuestionBankPage(props) {
    const [questions, setQuestions] = useState([]);
    const [questionBankName, setQuestionBankName] = useState("");
    const [createQuestionBank, setCreateQuestionBank] = useState(true);
    const [currentFile, setCurrentFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef(null);
    const assessmentQuestionRepository = useAssessmentQuestionRepository();

    const onUploadFile = (file) => {
        setLoading(true)
        const reader = new FileReader();
        reader.onload = () => {
            const arrayBuffer = reader.result;
            let workbook = read(arrayBuffer, { type: 'array' });
            let sheet = workbook.Sheets[workbook.SheetNames[0]];
            let data_json = utils.sheet_to_json(sheet);
            let idx = 1;
            for (const item of data_json) {
                item['id'] = item.id ?? ++idx;
            }
            setQuestions(parseJSONRow(data_json));
        };
        reader.onerror = err => console.error(err);
        reader.readAsArrayBuffer(file);
        setLoading(false)
    }

    const listContext = useList({
        data: questions,
        ids: questions.map((w) => w.id),
        loading,
        loaded: !loading,
    })


    const resetInput = () => {
        setQuestions([])
        setCurrentFile(null)
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
        }
    };

    const importQuestions = useCallback(async () => {
        if (!questionBankName) {
            alert("Informe o nome do novo banco de questões");
            return;
        }
        let chunks = []
        let returns = [];
        setLoading(true);
        setQuestions([])
        for (const item of questions) {
            chunks.push(item);
            if (chunks.length > 10) {
                await assessmentQuestionRepository.importQuestions(chunks, questionBankName, createQuestionBank).then(res => {
                    returns = [...returns, ...res.data.report]
                    setQuestions(prev => [...prev, ...res.data.report]);
                    setLoading(false);
                });
                chunks = [];
            }
        }
        if (chunks.length > 0) {
            await assessmentQuestionRepository.importQuestions(chunks, questionBankName, createQuestionBank).then(res => {
                returns = [...returns, ...res.data.report]
                setQuestions(prev => [...prev, ...res.data.report]);
                setLoading(false);
            });
        }
    }, [createQuestionBank, questionBankName, questions])

    const handleClick = () => {
        fileInputRef.current.click();
    };

    const { totalErrors, totalItems, totalLoaded, totalDup } = useMemo(() => {
        const totalItems = questions.length;
        const totalLoaded = questions.filter(w => w.success)?.length;
        const totalErrors = questions.filter(w => w.error)?.length;
        const totalDup = questions.filter(w => w.questionDuplicated)?.length;
        return { totalLoaded, totalErrors, totalItems, totalDup };
    }, [questions])

    return (
        <>
            <Typography variant={'h4'}>Importar Questõs</Typography>
            <Typography variant={'body1'}>
                Para importar questões baixa fazer o download <a href={'/banco-questoes-modelo-importacao.xlsx'} target={'_blank'}>deste modelo de planilha</a>, preenchê-la com as questões de
                respeitando os cabeçalhos e fazer o upload da mesmo no botão abaixo. Atenção, o upload deve ser feito de até no máximo
                300 questões por vez por motivos de performance</Typography>

            <div style={{ width: '100%', gap: 10, display: 'flex', flexDirection: 'row', margin: '20px 0 20px 0' }}>
                <Button
                    variant="contained"
                    component="label"
                    onClick={handleClick}
                    startIcon={loading ? <CircularProgress size={16} /> : <CloudUpload />}
                    disabled={loading}
                >
                    {currentFile?.name ?? "Upload Planilha"}
                </Button>

                <input
                    ref={fileInputRef}
                    type={'file'}
                    style={{ display: 'none' }}
                    accept={'.xls, .xlsx'}
                    onChange={evt => {
                        onUploadFile(evt.target.files[0])
                        setCurrentFile(evt.target.files[0])
                    }}
                />
                {
                    questions.length > 0 ? (
                        <Button
                            variant={'contained'}
                            startIcon={loading ? <CircularProgress size={16} /> : <SaveAltOutlined />}
                            disabled={loading}
                            onClick={() => importQuestions()}>
                            Importar
                        </Button>
                    ) : null
                }



                {
                    fileInputRef.current?.files && (
                        <Button disabled={loading} variant={'outlined'} onClick={() => resetInput()}>Cancelar</Button>
                    )
                }
            </div>
            {
                questions?.length > 0 && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                        <input
                            value={questionBankName}
                            onChange={evt => setQuestionBankName(evt.target.value)}
                            // style={{width: '450px'}}
                            placeholder={'Novo Banco de Questões'}
                            style={{
                                background: 'none',
                                width: '100%',
                                border: '1px solid gray',
                                borderRadius: '5px',
                                padding: 10,
                                color: 'white',
                            }}
                        />
                    </div>
                )
            }


            {
                questions.length > 0 && (
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'row', gap: 20, margin: '20px 0 20px 0' }}>
                        <Typography variant={'body1'}>Total de Questões: {totalItems}</Typography>
                        <Typography variant={'body1'}>Total de Importado: {totalLoaded}</Typography>
                        <Typography variant={'body1'}>Total de Duplicadas: {totalDup}</Typography>
                        <Typography variant={'body1'}>Total de Erros: {totalErrors}</Typography>
                    </div>
                )
            }
            <div style={{ maxWidth: '80vw', maxHeight: '65vh', overflow: 'auto' }}>
                {
                    loading ? (
                        <Loading />
                    ) : (
                        <ListContextProvider value={listContext}>
                            <Datagrid>
                                <FunctionField render={rec => <ReportCell record={rec} />} />
                                <TextField source={'name'} label={'Nome'} />
                                <TextField source={'themeAlias'} label={'Área'} />
                                <FunctionField source='tags' label={'Tags'} render={record => Array.isArray(record.tags) ? record.tags.join(',') : ''} />
                                <CoreTextFieldEllipseComponent source={'question'} label={'Questão'} length={100} />
                                <ShowQuestionItem />
                            </Datagrid>
                        </ListContextProvider>
                    )
                }
            </div>
        </>
    )
}

function OptionCell(props) {
    const length = 50;

    const ellipseText = useMemo(() => {
        if (props.option?.length > length) {
            return `${props.option?.substring(0, length - 1) + '...'}`;
        }
        return props.option;
    }, [props.option])

    if (props.isCorrect) {
        return (
            <div title={props.option} style={{ color: 'darkgreen', fontWeight: 'bold' }}>
                {ellipseText}
            </div>
        )
    }
    return <span title={props.option}>{ellipseText}</span>
}

function ShowQuestionItem(props) {
    const [showDialog, setShowDialog] = React.useState(false);


    return (
        <>
            {showDialog && (
                <Dialog open onClose={() => setShowDialog(false)} maxWidth={'lg'}  >
                    <DialogContent dividers  >
                        <Grid container direction={'column'} >

                            <Grid role={'row'} md={12}>
                                {props.record.tags.map(w => <Chip label={w} key={w} />)}
                                <Chip label={props.record.themeAlias} />
                            </Grid>

                            <br />
                            <Grid role={'row'} spacing={10}>
                                <Typography variant={'body2'} style={{ whiteSpace: '' }}>
                                    {props.record.question.split('\\n').map(w => (<p>{w}</p>))}
                                </Typography>
                            </Grid>
                            <br />
                            {
                                props.record.options.map((option, x) => (
                                    <Grid key={x} xs={12}>
                                        <Typography variant={'body2'} color={option.isCorrect ? 'primary' : undefined}> {x + 1} - {option.option}</Typography>
                                    </Grid>
                                ))
                            }
                        </Grid>
                    </DialogContent>
                </Dialog>
            )}
            <div onClick={() => setShowDialog(true)} style={{ cursor: 'pointer' }}>
                <DetailsOutlined />
            </div>
        </>

    )
}

function ReportCell(props) {
    const [statusOpen, setStatusOpen] = React.useState(false);
    if (typeof props.record.success === 'undefined') {
        return <CloudUpload title={'Aguardando importação'} />
    }
    if (props.record.success === true) {
        const color = props.record.questionDuplicated === true ? 'yellow' : 'green';
        return <CloudDone title={'Importado com sucesso'} style={{color}} />
    }
    return (
        <>
            <Dialog
                open={statusOpen}
                onClose={() => setStatusOpen(false)}
            >
                <DialogContent>
                    {props.record?.error}
                    {props.record.questionDuplicated && 'Questão ja existente na base de dados'}
                </DialogContent>
            </Dialog>
            <ErrorOutline
                color={'error'}
                style={{ cursor: 'pointer' }}
                onClick={() => setStatusOpen(prev => !prev)}
            />
        </>
    )
}

function parseJSONRow(data) {
    const parsedData = []
    for (const row of data) {
        const parsedRow = {
            id: row.id,
            name: '',
            question: '',
            themeAlias: '',
            tags: [],
            options: [],
        }
        for (const key of Object.keys(row)) {
            switch (key) {
                case 'NOME':
                    parsedRow.name = row[key];
                    break;
                case 'ENUNCIADO': parsedRow.question = row[key]; break;
                case 'AREA': parsedRow.themeAlias = row[key]; break;
                case 'TAGS': parsedRow.tags = row[key] ? row[key]?.toString()?.split(',') : []; break;
                case 'OPCAO_1':
                case 'OPCAO_2':
                case 'OPCAO_3':
                case 'OPCAO_4':
                case 'OPCAO_5':
                case 'OPCAO_6':
                    parsedRow.options.push({ option: row[key], isCorrect: false, }); break;
                case 'OPCAO_CORRETA': parsedRow.options.push({ option: row[key], isCorrect: true }); break;
                default: break;
            }
        }
        parsedData.push(parsedRow);
    }
    return parsedData;
}